"use client";

import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { updatePatient } from "@/lib/db/mutations/patients";
import { useRouter } from "next/navigation";
import React, { useEffect } from "react";
import Joyride, {
    CallBackProps,
    EVENTS,
    Placement,
    TooltipRenderProps,
} from "react-joyride";
import { useMount } from "react-use";

import { useAppContext } from "./context";

const tourSteps = [
    {
        target: "body",
        content: (
            <div>
                <p>Let&apos;s go through a quick walkthrough of the platform</p>
            </div>
        ),
        title: "Welcome to RTHM Intelligence!",
        placement: "center" as Placement,
        disableBeacon: true,
        disableOverlayClose: true,
        hideCloseButton: true,
    },
    {
        target: "[data-tour-id='header-dashboard-link']",
        content: (
            <div>
                <p>This is your dashboard.</p>
            </div>
        ),
        placement: "bottom" as Placement,
        disableBeacon: true,
        data: {
            next: "/dashboard",
        },
        disableOverlayClose: true,
        hideCloseButton: true,
        spotlightClicks: true,
        spotlightPadding: 0,
    },
    {
        target: "body",
        content: (
            <div>
                <p>
                    On the dashboard, you will be able to see your profile
                    completion. From uploading your labs to answering medically
                    curated questionnaires, each completion gives our system
                    more information to provide you with a better experience.
                </p>
            </div>
        ),
        placement: "center" as Placement,
        disableBeacon: true,
        disableOverlayClose: true,
        hideCloseButton: true,
    },
    {
        target: "[data-tour-id='header-ai-chat-link']",
        content: (
            <div>
                <p>Next up is the AI Chat.</p>
            </div>
        ),
        placement: "bottom" as Placement,
        disableBeacon: true,
        data: {
            prev: "/dashboard",
            next: "/chat",
        },
        disableOverlayClose: true,
        hideCloseButton: true,
        spotlightClicks: true,
        spotlightPadding: 0,
    },
    {
        target: "body",
        content: (
            <div>
                <p>
                    In the RTHM Health Chat, you can engage with all your health
                    information: treatments tried, lab results, research
                    theories and any question that you have about your
                    condition. Use it as a research co-pilot. This system is
                    consistently getting better and is being trained on your
                    conditions.
                </p>
            </div>
        ),
        placement: "center" as Placement,
        disableBeacon: true,
        disableOverlayClose: true,
        hideCloseButton: true,
    },
    {
        target: "[data-tour-id='header-orders-link']",
        content: (
            <div>
                <p>Let&apos;s check out your Orders.</p>
            </div>
        ),
        placement: "bottom" as Placement,
        disableBeacon: true,
        data: {
            prev: "/chat",
            next: "/orders",
        },
        disableOverlayClose: true,
        hideCloseButton: true,
        spotlightClicks: true,
        spotlightPadding: 0,
    },
    {
        target: "body",
        content: (
            <div>
                <p>
                    The Orders page is where you can view and manage your
                    medication orders, track their status, and access your
                    instructions. This is your central hub for all
                    medication-related activities.
                </p>
            </div>
        ),
        placement: "center" as Placement,
        disableBeacon: true,
        disableOverlayClose: true,
        hideCloseButton: true,
    },
    {
        target: "[data-tour-id='header-my-data-link']",
        content: (
            <div>
                <p>Now let&apos;s explore your Data.</p>
            </div>
        ),
        placement: "bottom" as Placement,
        disableBeacon: true,
        data: {
            prev: "/orders",
            next: "/my-data",
        },
        disableOverlayClose: true,
        hideCloseButton: true,
        spotlightClicks: true,
        spotlightPadding: 0,
    },
    {
        target: "body",
        content: (
            <div>
                <p>
                    The Data page is where you can find and manage all your
                    health information, including symptoms, diagnoses,
                    documents, and your health records connections. This is
                    where you will go to load new lab results PDFs. This
                    information is used as the basis of all your experience with
                    the AI.
                </p>
            </div>
        ),
        placement: "center" as Placement,
        disableBeacon: true,
        disableOverlayClose: true,
        hideCloseButton: true,
    },
    {
        target: "[data-tour-id='header-treatments-link']",
        content: (
            <div>
                <p>Next, let&apos;s check out Treatments.</p>
            </div>
        ),
        placement: "bottom" as Placement,
        disableBeacon: true,
        data: {
            prev: "/my-data",
            next: "/my-treatments",
        },
        disableOverlayClose: true,
        hideCloseButton: true,
        spotlightClicks: true,
        spotlightPadding: 0,
    },
    {
        target: "body",
        content: (
            <div>
                <p>
                    On the Treatments page, you can add your current and past
                    treatment and see personalized treatment suggestions based
                    on your health profile. We are consistently updating the
                    treatment suggestions to include the latest treatment
                    research in the space. Your experience with each treatment
                    helps the system generate better suggestions.
                </p>
            </div>
        ),
        placement: "center" as Placement,
        disableBeacon: true,
        disableOverlayClose: true,
        hideCloseButton: true,
    },
    {
        target: "[data-tour-id='header-services-link']",
        content: (
            <div>
                <p>Let&apos;s also explore our Services.</p>
            </div>
        ),
        placement: "bottom" as Placement,
        disableBeacon: true,
        data: {
            prev: "/my-treatments",
            next: "/services?tab=medications",
        },
        disableOverlayClose: true,
        hideCloseButton: true,
        spotlightClicks: true,
        spotlightPadding: 0,
    },
    {
        target: "body",
        content: (
            <div>
                <p>
                    The Services page showcases all the health services we
                    offer, from medications and lab testing to specialized
                    consultations. Here you can discover and book the services
                    that best fit your health needs.
                </p>
            </div>
        ),
        placement: "center" as Placement,
        disableBeacon: true,
        disableOverlayClose: true,
        hideCloseButton: true,
    },
    {
        target: "[data-tour-id='header-more-button']",
        content: (
            <div>
                <p>
                    Here you can find more options, including your profile, dark
                    mode, support and the ability to log out.
                </p>
            </div>
        ),
        placement: "left" as Placement,
        disableBeacon: true,
        disableOverlayClose: true,
        hideCloseButton: true,
    },
    {
        target: "body",
        content: (
            <div>
                <p>
                    That&apos;s a quick overview of the RTHM Intelligence app.
                    Now let&apos;s check out what the AI Chat can do.
                </p>
            </div>
        ),
        placement: "center" as Placement,
        disableBeacon: true,
        disableOverlayClose: true,
        hideCloseButton: true,
    },
];

// Custom Tooltip component with RTHM glassmorphic design
const Tooltip = ({
    continuous,
    index,
    step,
    backProps,
    closeProps,
    primaryProps,
    skipProps,
    isLastStep,
    tooltipProps,
}: TooltipRenderProps) => {
    const { title, content, placement } = step;

    // Special styling for center placement
    const isCentered = placement === "center";
    const centerStyles = isCentered
        ? {
              position: "fixed" as const,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
          }
        : {};

    return (
        <div
            {...tooltipProps}
            className="z-[100000] rounded-3xl bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-gray-800 dark:via-blue-950 dark:to-indigo-950 shadow-xl border border-white/20 dark:border-gray-700/30 backdrop-blur-sm"
            style={{
                maxWidth: "min(calc(100vw - 32px), 650px)",
                width: "auto",
                minWidth: window?.innerWidth < 640 ? "280px" : "350px",
                padding: window?.innerWidth < 640 ? "12px" : "16px",
                margin: isCentered ? "16px" : "8px",
                transition: "opacity 0.3s ease, transform 0.3s ease",
                ...centerStyles,
            }}
        >
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-gradient-to-br from-blue-400/20 to-indigo-400/20 blur-2xl pointer-events-none -z-10" />
            <div className="absolute bottom-0 left-0 -mb-4 -ml-4 h-32 w-32 rounded-full bg-gradient-to-tr from-purple-400/20 to-indigo-400/20 blur-2xl pointer-events-none -z-10" />

            {/* Content */}
            <div className="relative z-10">
                {/* Header */}
                <div className="flex flex-col gap-2 text-center sm:text-left mb-4">
                    {title && (
                        <h3 className="text-lg leading-none font-semibold break-words bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-800 bg-clip-text text-transparent dark:from-white dark:via-blue-200 dark:to-indigo-200">
                            {title}
                        </h3>
                    )}
                    <div className="text-gray-600 dark:text-gray-300 text-sm break-words">
                        {content}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex flex-wrap justify-between mt-6 gap-2">
                    <div className="flex flex-wrap gap-2">
                        {index > 0 && (
                            <Button
                                variant="outline"
                                {...backProps}
                                className="min-w-[70px] h-10 bg-gradient-to-r from-gray-200 to-gray-300 text-gray-700 dark:from-gray-700 dark:to-gray-800 dark:text-gray-300 hover:shadow-sm transition-all duration-200 transform hover:-translate-y-0.5"
                            >
                                Back
                            </Button>
                        )}
                        {index === 0 && !isLastStep && (
                            <Button
                                variant="outline"
                                {...skipProps}
                                onClick={() => {
                                    // Handle skip action because react-joyride doesnt right now. Issue #1172
                                    //Update patient state
                                    updatePatient({
                                        has_completed_app_tour: true,
                                    }).then((patientResponse) => {
                                        if (patientResponse.type === "error") {
                                            console.error(
                                                "Error updating patient:",
                                                patientResponse,
                                            );
                                        } else {
                                            const patient =
                                                patientResponse.data;
                                            if (patient.metriport_id) {
                                                // router.push("/chat");
                                                window.location.href = "/chat";
                                            } else {
                                                // router.push("/chat?query=What can you do?");
                                                window.location.href =
                                                    "/chat?query=What can you do?";
                                            }
                                        }
                                    });
                                }}
                                className="min-w-[70px] h-10 bg-gradient-to-r from-gray-200 to-gray-300 text-gray-700 dark:from-gray-700 dark:to-gray-800 dark:text-gray-300 hover:shadow-sm transition-all duration-200 transform hover:-translate-y-0.5"
                            >
                                Skip
                            </Button>
                        )}
                    </div>
                    <div>
                        {continuous && !isLastStep ? (
                            <Button
                                variant="default"
                                {...primaryProps}
                                className="min-w-[70px] h-10 bg-gradient-to-r from-blue-600 to-indigo-600 text-white transition-all duration-200 hover:shadow-lg transform hover:-translate-y-0.5"
                            >
                                Next
                            </Button>
                        ) : (
                            <Button
                                variant="default"
                                {...closeProps}
                                onClick={() => {
                                    // Handle skip action because react-joyride doesnt right now. Issue #1172
                                    //Update patient state
                                    updatePatient({
                                        has_completed_app_tour: true,
                                    }).then((patientResponse) => {
                                        if (patientResponse.type === "error") {
                                            console.error(
                                                "Error updating patient:",
                                                patientResponse,
                                            );
                                        } else {
                                            const patient =
                                                patientResponse.data;
                                            if (patient.metriport_id) {
                                                // router.push("/chat");
                                                window.location.href = "/chat";
                                            } else {
                                                // router.push("/chat?query=What can you do?");
                                                window.location.href =
                                                    "/chat?query=What can you do?";
                                            }
                                        }
                                    });
                                }}
                                className="min-w-[70px] h-10 bg-gradient-to-r from-blue-600 to-indigo-600 text-white transition-all duration-200 hover:shadow-lg transform hover:-translate-y-0.5"
                            >
                                Close
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const AppIntroductionTour = () => {
    const {
        setState,
        state: { run, stepIndex, steps, tourActive },
    } = useAppContext();
    const router = useRouter();
    const isMobile = useIsMobile();

    // Filter steps based on mobile/desktop
    const getFilteredSteps = () => {
        if (isMobile) {
            // On mobile, replace header-targeting steps with body-centered navigation steps
            return tourSteps.map((step, index) => {
                const headerTargets = [
                    "[data-tour-id='header-dashboard-link']",
                    "[data-tour-id='header-ai-chat-link']",
                    "[data-tour-id='header-orders-link']",
                    "[data-tour-id='header-my-data-link']",
                    "[data-tour-id='header-treatments-link']",
                    "[data-tour-id='header-services-link']",
                    "[data-tour-id='header-more-button']",
                ];

                if (headerTargets.includes(step.target)) {
                    // Replace header-targeting steps with body-centered navigation steps
                    return {
                        ...step,
                        target: "body",
                        placement: "center" as Placement,
                        content: (
                            <div>
                                <p>
                                    {step.target ===
                                        "[data-tour-id='header-dashboard-link']" &&
                                        "Let's navigate to your Dashboard to see your profile completion and health overview."}
                                    {step.target ===
                                        "[data-tour-id='header-ai-chat-link']" &&
                                        "Now let's check out the AI Chat where you can engage with all your health information."}
                                    {step.target ===
                                        "[data-tour-id='header-orders-link']" &&
                                        "Next, let's explore your Orders page to manage your medications."}
                                    {step.target ===
                                        "[data-tour-id='header-my-data-link']" &&
                                        "Let's visit your Data page to manage your health information."}
                                    {step.target ===
                                        "[data-tour-id='header-treatments-link']" &&
                                        "Now let's check out the Treatments page for personalized suggestions."}
                                    {step.target ===
                                        "[data-tour-id='header-services-link']" &&
                                        "Let's explore our Services page to see what we offer."}
                                    {step.target ===
                                        "[data-tour-id='header-more-button']" &&
                                        "That covers the main sections of the app. You can find additional options like your profile, dark mode, and support in the mobile menu."}
                                </p>
                            </div>
                        ),
                    };
                }
                return step;
            });
        }
        return tourSteps;
    };

    // Prevent background scrolling when tour is active
    useEffect(() => {
        if (run && tourActive) {
            // Store original overflow values
            const originalOverflow = document.body.style.overflow;
            const originalPosition = document.body.style.position;

            // Prevent scrolling
            document.body.style.overflow = "hidden";
            document.body.style.position = "fixed";
            document.body.style.width = "100%";
            document.body.style.height = "100%";

            // Cleanup function to restore original styles
            return () => {
                document.body.style.overflow = originalOverflow;
                document.body.style.position = originalPosition;
                document.body.style.width = "";
                document.body.style.height = "";
            };
        }
    }, [run, tourActive]);

    useMount(() => {
        // Only set steps and run if the tour is active
        if (tourActive) {
            // router.push("/dashboard");
            setState({
                steps: getFilteredSteps(),
                run: true, // Ensure the tour runs when the component mounts
            });
        }
    });

    const handleCallback = (data: CallBackProps) => {
        const { action, index, step, type } = data;

        // Safely extract next and previous from step.data
        const next = step.data?.next;
        const previous = step.data?.previous || step.data?.prev;
        const isPreviousAction = action === "prev";

        if (type === EVENTS.STEP_AFTER) {
            // Calculate the next step index
            const nextStepIndex = isPreviousAction ? index - 1 : index + 1;

            // For steps that navigate to a new page
            if (next || (isPreviousAction && previous)) {
                // Keep the tour running during navigation to prevent flickering
                setState({
                    stepIndex: nextStepIndex,
                    run: true, // Keep the tour running
                });

                // Use router.push with { scroll: false } to prevent page jumps
                router.push(isPreviousAction && previous ? previous : next, {
                    scroll: false,
                });
            }
            // For steps that don't navigate but just advance
            else {
                setState({
                    stepIndex: nextStepIndex,
                });
            }
        } else if (type === EVENTS.TOUR_END) {
            //Update patient state
            // updatePatient({
            //     has_completed_app_tour: true,
            // }).then((patientResponse) => {
            //     if (patientResponse.type === "error") {
            //         console.error("Error updating patient:", patientResponse);
            //     } else {
            //         const patient = patientResponse.data;
            //         if (patient.metriport_id) {
            //             // router.push("/chat");
            //             window.location.href = "/chat";
            //         } else {
            //             // router.push("/chat?query=What can you do?");
            //             window.location.href = "/chat?query=What can you do?";
            //         }
            //     }
            // });
            setState({ run: false, stepIndex: 0, tourActive: false });
        }
    };

    return (
        <Joyride
            steps={getFilteredSteps()}
            run={run} // Directly use the run prop from TourProvider
            stepIndex={stepIndex}
            continuous
            callback={handleCallback}
            styles={{
                options: {
                    zIndex: 10000, // Ensure tour is above other elements
                    arrowColor: "#fff",
                    backgroundColor: "#fff",
                    primaryColor: "#007bff", // Example primary color
                    textColor: "#333",
                },
                spotlight: {
                    borderRadius: "4px",
                    transition:
                        "opacity 0.5s ease, transform 0.5s ease, width 0.5s ease, height 0.5s ease",
                    zIndex: 99999,
                },
                tooltip: {
                    backgroundColor: "transparent",
                    borderRadius: 0,
                    boxShadow: "none",
                    filter: "none",
                    transition: "opacity 0.3s ease",
                },
                tooltipContainer: {
                    textAlign: "left",
                },
                overlay: {
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    transition: "opacity 0.3s ease",
                },
            }}
            disableOverlayClose
            disableCloseOnEsc
            disableScrolling
            tooltipComponent={Tooltip}
        />
    );
};

export default AppIntroductionTour;
